import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Server, Plus, Pencil, Trash2, Activity, Eye, EyeOff, Tv, Wifi, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function TechnicalImplementation({ customerId, customerName }) {
  const { API, user } = useAuth();
  const [implementations, setImplementations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [activeFormTab, setActiveFormTab] = useState('mikrotik');

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const canEdit = hasRole(['admin', 'noc']);

  const defaultForm = {
    implementation_type: 'customer_premises',
    mikrotik_ip: '',
    mikrotik_username: '',
    mikrotik_password: '',
    // PPPOE
    pppoe_enabled: false,
    pppoe_username: '',
    pppoe_password: '',
    pppoe_service_name: '',
    // IPOE
    ipoe_enabled: false,
    ipoe_vlan: '',
    ipoe_ip_address: '',
    ipoe_gateway: '',
    // Routes
    static_routes: '',
    // Centralised
    ip_pools_used: '',
    centralised_mikrotik_ip: '',
    centralised_mikrotik_name: '',
    centralised_static_routes: '',
    public_ip_assigned: '',
    // CDN
    cdn_enabled: false,
    cdn_name: '',
    cdn_input_ip: '',
    cdn_output_ip: '',
    cdn_username: '',
    cdn_password: '',
    cdn_edge_url: '',
    cdn_notes: '',
    // Other
    add_to_monitor: false,
    notes: ''
  };

  const [form, setForm] = useState(defaultForm);

  const fetchImplementations = async () => {
    try {
      const url = customerId 
        ? `${API}/technical-implementations?customer_id=${customerId}`
        : `${API}/technical-implementations`;
      const response = await axios.get(url);
      setImplementations(response.data);
    } catch (error) {
      toast.error('Failed to fetch implementations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchImplementations(); }, [API, customerId]);

  const resetForm = () => {
    setForm(defaultForm);
    setEditId(null);
    setActiveFormTab('mikrotik');
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...form };
      if (customerId) payload.customer_id = customerId;

      if (editId) {
        await axios.put(`${API}/technical-implementations/${editId}`, payload);
        toast.success('Implementation updated');
      } else {
        if (!customerId) {
          toast.error('Customer ID is required');
          return;
        }
        await axios.post(`${API}/technical-implementations`, payload);
        toast.success('Implementation added');
      }
      setDialog(false);
      resetForm();
      fetchImplementations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save implementation');
    }
  };

  const handleEdit = (impl) => {
    setEditId(impl.implementation_id);
    setForm({
      implementation_type: impl.implementation_type || 'customer_premises',
      mikrotik_ip: impl.mikrotik_ip || '',
      mikrotik_username: impl.mikrotik_username || '',
      mikrotik_password: impl.mikrotik_password || '',
      pppoe_enabled: impl.pppoe_enabled || false,
      pppoe_username: impl.pppoe_username || '',
      pppoe_password: impl.pppoe_password || '',
      pppoe_service_name: impl.pppoe_service_name || '',
      ipoe_enabled: impl.ipoe_enabled || false,
      ipoe_vlan: impl.ipoe_vlan || '',
      ipoe_ip_address: impl.ipoe_ip_address || '',
      ipoe_gateway: impl.ipoe_gateway || '',
      static_routes: impl.static_routes || '',
      ip_pools_used: impl.ip_pools_used || '',
      centralised_mikrotik_ip: impl.centralised_mikrotik_ip || '',
      centralised_mikrotik_name: impl.centralised_mikrotik_name || '',
      centralised_static_routes: impl.centralised_static_routes || '',
      public_ip_assigned: impl.public_ip_assigned || '',
      cdn_enabled: impl.cdn_enabled || false,
      cdn_name: impl.cdn_name || '',
      cdn_input_ip: impl.cdn_input_ip || '',
      cdn_output_ip: impl.cdn_output_ip || '',
      cdn_username: impl.cdn_username || '',
      cdn_password: impl.cdn_password || '',
      cdn_edge_url: impl.cdn_edge_url || '',
      cdn_notes: impl.cdn_notes || '',
      add_to_monitor: impl.add_to_monitor || false,
      notes: impl.notes || ''
    });
    setDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this implementation?')) return;
    try {
      await axios.delete(`${API}/technical-implementations/${id}`);
      toast.success('Implementation deleted');
      fetchImplementations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const togglePassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      online: 'bg-emerald-100 text-emerald-700',
      offline: 'bg-red-100 text-red-700',
      unknown: 'bg-slate-100 text-slate-700'
    };
    return colors[status] || colors.unknown;
  };

  return (
    <div className="space-y-4" data-testid="technical-implementation">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Technical Implementation</h3>
          <p className="text-sm text-slate-500">BNG/Mikrotik, PPPOE/IPOE & IPTV CDN configuration</p>
        </div>
        {canEdit && (
          <Button onClick={() => { resetForm(); setDialog(true); }} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Implementation
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading...</div>
      ) : implementations.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Server className="h-12 w-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No technical implementation configured</p>
            {canEdit && (
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setDialog(true); }}>
                <Plus className="h-4 w-4 mr-1" /> Add First Implementation
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {implementations.map((impl) => (
            <Card key={impl.implementation_id}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Server className="h-4 w-4" />
                      {impl.implementation_type === 'customer_premises' ? 'Customer Premises' : 'Centralised'} Implementation
                    </CardTitle>
                    <CardDescription>
                      {impl.mikrotik_ip && `Mikrotik: ${impl.mikrotik_ip}`}
                      {impl.centralised_mikrotik_name && `Centralised: ${impl.centralised_mikrotik_name}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {impl.add_to_monitor && (
                      <Badge className={getStatusBadge(impl.monitor_status)}>
                        <Activity className="h-3 w-3 mr-1" />
                        {impl.monitor_status || 'unknown'}
                      </Badge>
                    )}
                    {impl.cdn_enabled && (
                      <Badge className="bg-violet-100 text-violet-700">
                        <Tv className="h-3 w-3 mr-1" />
                        CDN
                      </Badge>
                    )}
                    {impl.pppoe_enabled && (
                      <Badge className="bg-sky-100 text-sky-700">PPPOE</Badge>
                    )}
                    {impl.ipoe_enabled && (
                      <Badge className="bg-emerald-100 text-emerald-700">IPOE</Badge>
                    )}
                    {canEdit && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(impl)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(impl.implementation_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {/* Mikrotik/BNG Details */}
                  <AccordionItem value="mikrotik">
                    <AccordionTrigger className="text-sm">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        Mikrotik/BNG Details
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {impl.implementation_type === 'customer_premises' ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Mikrotik IP</p>
                            <p className="font-mono font-medium">{impl.mikrotik_ip || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Username</p>
                            <p className="font-medium">{impl.mikrotik_username || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Password</p>
                            <div className="flex items-center gap-1">
                              <p className="font-medium font-mono">
                                {showPasswords[`${impl.implementation_id}_mikrotik`] ? impl.mikrotik_password : '••••••••'}
                              </p>
                              {impl.mikrotik_password && (
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => togglePassword(`${impl.implementation_id}_mikrotik`)}>
                                  {showPasswords[`${impl.implementation_id}_mikrotik`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                              )}
                            </div>
                          </div>
                          {impl.static_routes && (
                            <div className="col-span-4">
                              <p className="text-slate-500">Static Routes</p>
                              <p className="font-mono text-xs bg-slate-50 p-2 rounded">{impl.static_routes}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Centralised Mikrotik</p>
                            <p className="font-medium">{impl.centralised_mikrotik_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Mikrotik IP</p>
                            <p className="font-mono font-medium">{impl.centralised_mikrotik_ip || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">IP Pools</p>
                            <p className="font-mono text-xs">{impl.ip_pools_used || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Public IP</p>
                            <p className="font-mono font-medium">{impl.public_ip_assigned || '-'}</p>
                          </div>
                          {impl.centralised_static_routes && (
                            <div className="col-span-4">
                              <p className="text-slate-500">Static Routes</p>
                              <p className="font-mono text-xs bg-slate-50 p-2 rounded">{impl.centralised_static_routes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  {/* PPPOE Details */}
                  {impl.pppoe_enabled && (
                    <AccordionItem value="pppoe">
                      <AccordionTrigger className="text-sm">
                        <div className="flex items-center gap-2">
                          <Wifi className="h-4 w-4 text-sky-600" />
                          PPPOE Configuration
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Username</p>
                            <p className="font-medium">{impl.pppoe_username || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Password</p>
                            <div className="flex items-center gap-1">
                              <p className="font-medium font-mono">
                                {showPasswords[`${impl.implementation_id}_pppoe`] ? impl.pppoe_password : '••••••••'}
                              </p>
                              {impl.pppoe_password && (
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => togglePassword(`${impl.implementation_id}_pppoe`)}>
                                  {showPasswords[`${impl.implementation_id}_pppoe`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-slate-500">Service Name</p>
                            <p className="font-medium">{impl.pppoe_service_name || '-'}</p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {/* IPOE Details */}
                  {impl.ipoe_enabled && (
                    <AccordionItem value="ipoe">
                      <AccordionTrigger className="text-sm">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-emerald-600" />
                          IPOE Configuration
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">VLAN</p>
                            <p className="font-mono font-medium">{impl.ipoe_vlan || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">IP Address</p>
                            <p className="font-mono font-medium">{impl.ipoe_ip_address || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Gateway</p>
                            <p className="font-mono font-medium">{impl.ipoe_gateway || '-'}</p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {/* IPTV CDN Details */}
                  {impl.cdn_enabled && (
                    <AccordionItem value="cdn">
                      <AccordionTrigger className="text-sm">
                        <div className="flex items-center gap-2">
                          <Tv className="h-4 w-4 text-violet-600" />
                          IPTV CDN Details
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">CDN Name</p>
                            <p className="font-medium">{impl.cdn_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Input IP</p>
                            <p className="font-mono font-medium">{impl.cdn_input_ip || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Output IP</p>
                            <p className="font-mono font-medium">{impl.cdn_output_ip || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Edge URL</p>
                            <p className="font-mono text-xs break-all">{impl.cdn_edge_url || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Username</p>
                            <p className="font-medium">{impl.cdn_username || '-'}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Password</p>
                            <div className="flex items-center gap-1">
                              <p className="font-medium font-mono">
                                {showPasswords[`${impl.implementation_id}_cdn`] ? impl.cdn_password : '••••••••'}
                              </p>
                              {impl.cdn_password && (
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => togglePassword(`${impl.implementation_id}_cdn`)}>
                                  {showPasswords[`${impl.implementation_id}_cdn`] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </Button>
                              )}
                            </div>
                          </div>
                          {impl.cdn_notes && (
                            <div className="col-span-4">
                              <p className="text-slate-500">CDN Notes</p>
                              <p className="text-xs bg-slate-50 p-2 rounded">{impl.cdn_notes}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                </Accordion>

                {impl.notes && (
                  <div className="mt-4 p-3 bg-slate-50 rounded text-sm">
                    <p className="text-slate-500 mb-1">General Notes</p>
                    <p>{impl.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit' : 'Add'} Technical Implementation</DialogTitle>
            <DialogDescription>Configure BNG/Mikrotik, PPPOE/IPOE, and IPTV CDN details</DialogDescription>
          </DialogHeader>

          {/* Implementation Type Selection */}
          <div className="space-y-2 mb-4">
            <Label>Implementation Type</Label>
            <Select value={form.implementation_type} onValueChange={(v) => setForm({ ...form, implementation_type: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer_premises">Customer Premises (BNG/Mikrotik on site)</SelectItem>
                <SelectItem value="centralised">Centralised (Shared BNG/Mikrotik)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Tabs value={activeFormTab} onValueChange={setActiveFormTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="mikrotik">Mikrotik/BNG</TabsTrigger>
              <TabsTrigger value="pppoe">PPPOE</TabsTrigger>
              <TabsTrigger value="ipoe">IPOE</TabsTrigger>
              <TabsTrigger value="cdn">IPTV CDN</TabsTrigger>
            </TabsList>

            {/* Mikrotik/BNG Tab */}
            <TabsContent value="mikrotik" className="space-y-4 mt-4">
              {form.implementation_type === 'customer_premises' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mikrotik IP Address</Label>
                      <Input
                        value={form.mikrotik_ip}
                        onChange={(e) => setForm({ ...form, mikrotik_ip: e.target.value })}
                        placeholder="192.168.1.1"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        value={form.mikrotik_username}
                        onChange={(e) => setForm({ ...form, mikrotik_username: e.target.value })}
                        placeholder="admin"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={form.mikrotik_password}
                      onChange={(e) => setForm({ ...form, mikrotik_password: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Static Routes</Label>
                    <Textarea
                      value={form.static_routes}
                      onChange={(e) => setForm({ ...form, static_routes: e.target.value })}
                      placeholder="10.0.0.0/8 via 192.168.1.1"
                      rows={3}
                      className="font-mono text-sm"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Centralised Mikrotik/BNG Name</Label>
                      <Input
                        value={form.centralised_mikrotik_name}
                        onChange={(e) => setForm({ ...form, centralised_mikrotik_name: e.target.value })}
                        placeholder="BNG-Core-01"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Mikrotik/BNG IP</Label>
                      <Input
                        value={form.centralised_mikrotik_ip}
                        onChange={(e) => setForm({ ...form, centralised_mikrotik_ip: e.target.value })}
                        placeholder="10.0.0.1"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>IP Pools Used</Label>
                      <Textarea
                        value={form.ip_pools_used}
                        onChange={(e) => setForm({ ...form, ip_pools_used: e.target.value })}
                        placeholder="Pool-1: 10.10.0.0/24"
                        rows={2}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Public IP Assigned</Label>
                      <Input
                        value={form.public_ip_assigned}
                        onChange={(e) => setForm({ ...form, public_ip_assigned: e.target.value })}
                        placeholder="203.0.113.10"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Static Routes</Label>
                    <Textarea
                      value={form.centralised_static_routes}
                      onChange={(e) => setForm({ ...form, centralised_static_routes: e.target.value })}
                      placeholder="Route configurations"
                      rows={3}
                      className="font-mono text-sm"
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* PPPOE Tab */}
            <TabsContent value="pppoe" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2 p-3 bg-sky-50 rounded-lg">
                <Checkbox
                  id="pppoe_enabled"
                  checked={form.pppoe_enabled}
                  onCheckedChange={(checked) => setForm({ ...form, pppoe_enabled: checked })}
                />
                <Label htmlFor="pppoe_enabled" className="text-sm font-medium cursor-pointer">
                  Enable PPPOE Configuration
                </Label>
              </div>
              {form.pppoe_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>PPPOE Username</Label>
                      <Input
                        value={form.pppoe_username}
                        onChange={(e) => setForm({ ...form, pppoe_username: e.target.value })}
                        placeholder="pppoe_user"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>PPPOE Password</Label>
                      <Input
                        type="password"
                        value={form.pppoe_password}
                        onChange={(e) => setForm({ ...form, pppoe_password: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Name</Label>
                    <Input
                      value={form.pppoe_service_name}
                      onChange={(e) => setForm({ ...form, pppoe_service_name: e.target.value })}
                      placeholder="e.g., ANT-PPPOE"
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* IPOE Tab */}
            <TabsContent value="ipoe" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2 p-3 bg-emerald-50 rounded-lg">
                <Checkbox
                  id="ipoe_enabled"
                  checked={form.ipoe_enabled}
                  onCheckedChange={(checked) => setForm({ ...form, ipoe_enabled: checked })}
                />
                <Label htmlFor="ipoe_enabled" className="text-sm font-medium cursor-pointer">
                  Enable IPOE Configuration
                </Label>
              </div>
              {form.ipoe_enabled && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>VLAN ID</Label>
                    <Input
                      value={form.ipoe_vlan}
                      onChange={(e) => setForm({ ...form, ipoe_vlan: e.target.value })}
                      placeholder="100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IP Address</Label>
                    <Input
                      value={form.ipoe_ip_address}
                      onChange={(e) => setForm({ ...form, ipoe_ip_address: e.target.value })}
                      placeholder="10.0.0.10"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Gateway</Label>
                    <Input
                      value={form.ipoe_gateway}
                      onChange={(e) => setForm({ ...form, ipoe_gateway: e.target.value })}
                      placeholder="10.0.0.1"
                      className="font-mono"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* IPTV CDN Tab */}
            <TabsContent value="cdn" className="space-y-4 mt-4">
              <div className="flex items-center space-x-2 p-3 bg-violet-50 rounded-lg">
                <Checkbox
                  id="cdn_enabled"
                  checked={form.cdn_enabled}
                  onCheckedChange={(checked) => setForm({ ...form, cdn_enabled: checked })}
                />
                <Label htmlFor="cdn_enabled" className="text-sm font-medium cursor-pointer">
                  Enable IPTV CDN Configuration
                </Label>
              </div>
              {form.cdn_enabled && (
                <>
                  <div className="space-y-2">
                    <Label>CDN Name</Label>
                    <Input
                      value={form.cdn_name}
                      onChange={(e) => setForm({ ...form, cdn_name: e.target.value })}
                      placeholder="e.g., Customer IPTV CDN"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Input IP</Label>
                      <Input
                        value={form.cdn_input_ip}
                        onChange={(e) => setForm({ ...form, cdn_input_ip: e.target.value })}
                        placeholder="Source IP address"
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Output IP</Label>
                      <Input
                        value={form.cdn_output_ip}
                        onChange={(e) => setForm({ ...form, cdn_output_ip: e.target.value })}
                        placeholder="Destination IP address"
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Login Username</Label>
                      <Input
                        value={form.cdn_username}
                        onChange={(e) => setForm({ ...form, cdn_username: e.target.value })}
                        placeholder="CDN username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Login Password</Label>
                      <Input
                        type="password"
                        value={form.cdn_password}
                        onChange={(e) => setForm({ ...form, cdn_password: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Edge URL</Label>
                    <Input
                      value={form.cdn_edge_url}
                      onChange={(e) => setForm({ ...form, cdn_edge_url: e.target.value })}
                      placeholder="https://edge.cdn.example.com"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CDN Notes</Label>
                    <Textarea
                      value={form.cdn_notes}
                      onChange={(e) => setForm({ ...form, cdn_notes: e.target.value })}
                      placeholder="Additional CDN configuration notes..."
                      rows={2}
                    />
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>

          <div className="space-y-4 mt-4 pt-4 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="add_to_monitor"
                checked={form.add_to_monitor}
                onCheckedChange={(checked) => setForm({ ...form, add_to_monitor: checked })}
              />
              <Label htmlFor="add_to_monitor" className="text-sm font-normal cursor-pointer">
                Add to Ping Monitor (1 min interval)
              </Label>
            </div>
            <div className="space-y-2">
              <Label>General Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editId ? 'Update' : 'Add'} Implementation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
